import { defineMessages, useIntl } from 'react-intl';

import { Modal } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const AddToBagUnavailableIngredientsBody = (props: Props) => {
  const { count, productName, locationName, isReplacementRequired } = props;

  const { formatMessage } = useIntl();

  // ─── Derived ─────────────────────────────────────────────────────────

  const message = getBodyMessage({ count, isReplacementRequired });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal.BodyText sizeMatch={['16']}>
      {formatMessage(message, { location_name: locationName, product_name: productName })}
    </Modal.BodyText>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getBodyMessage(params: GetBodyMessageParams) {
  const { count, isReplacementRequired } = params;

  const hasSingleIngredient = count === 1;

  if (isReplacementRequired) {
    return hasSingleIngredient
      ? messages.singleIngredientReplacementRequired
      : messages.multipleIngredientsReplacementRequired;
  }

  return hasSingleIngredient ? messages.singleIngredient : messages.multipleIngredients;
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  singleIngredient: {
    defaultMessage:
      'This ingredient is usually in the {product_name}, but {location_name} is currently out of stock. You can add it without it, or customize it to pick a replacement.',
    description: 'Add To Bag | Unavailable ingredients | Body (single ingredient)',
  },
  multipleIngredients: {
    defaultMessage:
      'These ingredients are usually in the {product_name}, but {location_name} is currently out of stock. You can add it without them, or customize it to pick a replacement.',
    description: 'Add To Bag | Unavailable ingredients | Body (multiple ingredients)',
  },
  singleIngredientReplacementRequired: {
    defaultMessage:
      'This ingredient is usually in the {product_name}, but {location_name} is currently out of stock. Customize it to pick a replacement before adding it to your bag.',
    description:
      'Add To Bag | Unavailable ingredients | Body when a replacement is required (single ingredient)',
  },
  multipleIngredientsReplacementRequired: {
    defaultMessage:
      'These ingredients are usually in the {product_name}, but {location_name} is currently out of stock. Customize it to pick a replacement before adding it to your bag.',
    description:
      'Add To Bag | Unavailable ingredients | Body when a replacement is required (multiple ingredients)',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  count: number;
  productName: string;
  locationName: string;
  isReplacementRequired: boolean;
};

type GetBodyMessageParams = Pick<Props, 'count' | 'isReplacementRequired'>;
